import React, { useState, useEffect, Fragment, useCallback } from 'react'; // Added Fragment, useCallback
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react'; // Added for mobile menu transition/overlay
import axios from 'axios';
import { getCalApi } from "@calcom/embed-react";
import { setDocumentTitle, PAGE_TITLES, DEFAULT_TITLE } from '../../utils/DocumentTitle';
import UserProfileModal from './UserProfileModal';
import TermsAndConditions from '../auth/TermsAndConditions'; // Import Terms component
import { hasPermission } from '../../utils/permissions';
import { useSocket } from '../../contexts/SocketContext'; // Import useSocket
import ConfirmationModal from '../common/ConfirmationModal';
import useModuleSettings from '../../hooks/useModuleSettings';

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { socket, isConnected } = useSocket(); // Use socket context
  const { moduleSettings, loading: moduleLoading } = useModuleSettings();
  // Get sidebar state from localStorage
  const getSavedSidebarState = () => {
    const savedState = localStorage.getItem('sidebarMinimized');
    return savedState === 'true';
  };
  
  const [user, setUser] = useState({ name: 'User' });
  const [loading, setLoading] = useState(true);
  const [sidebarMinimized, setSidebarMinimized] = useState(getSavedSidebarState()); // Load from localStorage
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [overdueTasks, setOverdueTasks] = useState(0);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [sidebarColor, setSidebarColor] = useState('#1e40af'); // Default blue-800
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [needsToSignTerms, setNeedsToSignTerms] = useState(false); // State for terms check
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // State for mobile hamburger menu
  const [showBookingButton, setShowBookingButton] = useState(false); // State for booking button
  const [showDismissConfirm, setShowDismissConfirm] = useState(false); // State for dismiss confirmation

  // Define fetchUserProfile function outside useEffect so it can be passed as prop
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return null; // Return null if no token
      }

      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.get('/api/auth/me', config);
      const fetchedUser = response.data;
      setUser(fetchedUser);
      localStorage.setItem('user', JSON.stringify(fetchedUser)); // Store user data

      // Show booking button if onboarding is complete, company is new, and user is admin or superuser
      if (fetchedUser.company_onboarding_complete && fetchedUser.company_id) {
        // Only show to admins and superusers
        const isAdminOrSuperuser = ['admin', 'superuser'].includes(fetchedUser.role);

        if (isAdminOrSuperuser) {
          // Get company creation date from company data
          const companyCreatedAt = new Date(fetchedUser.company_created_at || new Date());
          const daysSinceCreation = Math.floor((new Date() - companyCreatedAt) / (1000 * 60 * 60 * 24));

          // Show booking button for companies less than 30 days old AND if not dismissed
          const isDismissed = localStorage.getItem('calButtonDismissed') === 'true';
          if (daysSinceCreation < 30 && !isDismissed) {
            setShowBookingButton(true);
          }
        }
      }
      
      // Check if the user is a tutor and needs to sign terms
      const TUTOR_ROLE_ID = 4; // Assuming 4 is the role ID for Tutor
      if (fetchedUser.role_id === TUTOR_ROLE_ID && !fetchedUser.terms_signed_at) {
        console.log('Tutor needs to sign terms.');
        setNeedsToSignTerms(true);
      } else {
        setNeedsToSignTerms(false); // Ensure it's false if already signed or not a tutor
      }
      return fetchedUser; // Return the fetched user data
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user'); // Clear user data on auth error
        navigate('/login');
      }
      setNeedsToSignTerms(false); // Ensure state is false on error
      return null; // Return null on error
    } finally {
      setLoading(false); // Set loading false after fetch attempt
    }
  };

  // Fetch unread messages count (wrapped in useCallback)
  const fetchUnreadMessages = useCallback(async () => {
    // Added check for user object before fetching
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (!currentUser || !hasPermission('chat', 'access')) {
        // console.log("Skipping unread fetch: No user or no permission");
        setUnreadMessages(0); // Ensure count is 0 if no permission
        return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.get('/api/chat/unread', config);
      const totalUnread = (response.data.teamChatUnreadCount || 0) + (response.data.dmUnreadCount || 0);
      setUnreadMessages(totalUnread);
    } catch (error) {
      // Don't log error if it's just a 403 (likely due to permissions)
      if (!(error.response && error.response.status === 403)) {
        console.error('Error fetching unread messages:', error);
      }
       setUnreadMessages(0); // Reset count on error
    }
  }, []); // No dependencies needed

  // Fetch overdue tasks count (wrapped in useCallback)
  const fetchOverdueTasks = useCallback(async () => {
     const currentUser = JSON.parse(localStorage.getItem('user'));
     if (!currentUser || !hasPermission('tasks', 'access')) {
        // console.log("Skipping overdue fetch: No user or no permission");
        setOverdueTasks(0);
        return;
     }
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.get('/api/tasks/overdue-count', config);
      setOverdueTasks(response.data.count);
    } catch (error) {
       if (!(error.response && error.response.status === 403)) {
         console.error('Error fetching overdue tasks count:', error);
       }
       setOverdueTasks(0); // Reset count on error
    }
  }, []); // No dependencies needed

  // Initialize Cal.com booking button for new companies
  useEffect(() => {
    let calInstance = null;
    
    if (showBookingButton) {
      (async function() {
        try {
          // Initialize Cal only once with a single namespace
          calInstance = await getCalApi({"namespace":"strategy"});
          
          // Configure the UI first (only once)
          calInstance("ui", {"hideEventTypeDetails":false,"layout":"month_view"});
          
          // Then add the floating button
          calInstance("floatingButton", {
            "calLink":"leadbooster/strategy",
            "config":{"layout":"month_view"},
            "buttonText":"Get FREE setup help",
            "buttonColor":"#3048b4"
          });
          
          // Add a dismiss button - try multiple times to ensure Cal button is rendered
          let attempts = 0;
          const tryAddDismissButton = () => {
            attempts++;
            // Look for the Cal.com button using multiple possible selectors
            const calButton = document.querySelector('[data-cal-namespace="strategy"], [data-cal-link*="leadbooster"], .Cal__floating-button, button[data-cal-trigger]');
            
            if (calButton || attempts > 10) {
              // Always add the dismiss button, positioned where Cal button usually appears
              const dismissBtn = document.createElement('button');
              dismissBtn.innerHTML = '×';
              dismissBtn.style.cssText = `
                position: fixed;
                bottom: 65px;
                right: 35px;
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background-color: #ef4444;
                color: white;
                border: none;
                cursor: pointer;
                font-size: 16px;
                font-weight: bold;
                z-index: 2147483647;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s ease;
              `;
              dismissBtn.title = 'Hide booking button permanently';
              dismissBtn.onmouseover = () => {
                dismissBtn.style.backgroundColor = '#dc2626';
                dismissBtn.style.transform = 'scale(1.1)';
              };
              dismissBtn.onmouseout = () => {
                dismissBtn.style.backgroundColor = '#ef4444';
                dismissBtn.style.transform = 'scale(1)';
              };
              dismissBtn.onclick = () => {
                setShowDismissConfirm(true);
              };
              document.body.appendChild(dismissBtn);
            } else {
              // Try again after a short delay
              setTimeout(tryAddDismissButton, 500);
            }
          };
          
          // Start trying to add the dismiss button
          setTimeout(tryAddDismissButton, 100);
          
          console.log("Cal.com button initialized");
        } catch (error) {
          console.error("Error initializing Cal.com:", error);
        }
      })();
    }
    
    return () => {
      // Remove the floating button when component unmounts
      if (window.Cal && window.Cal.ns?.strategy) {
        try {
          window.Cal.ns.strategy("floatingButton", { calLink: "" }); // Passing empty calLink removes the button
          console.log("Cal.com button removed");
        } catch (e) {
          console.error("Failed to remove Cal.com button during cleanup:", e);
        }
      }
      // Also remove any dismiss buttons we created
      const dismissBtns = document.querySelectorAll('button[title="Hide booking button"]');
      dismissBtns.forEach(btn => btn.remove());
    };
  }, [showBookingButton]);
  
  // Fetch initial counts and set up polling for overdue tasks
  useEffect(() => {
    fetchUnreadMessages(); // Fetch initial unread messages
    fetchOverdueTasks();   // Fetch initial overdue tasks

    // Poll only for overdue tasks every 30 seconds
    const taskInterval = setInterval(fetchOverdueTasks, 30000);

    return () => clearInterval(taskInterval);
  }, [fetchUnreadMessages, fetchOverdueTasks]); // Include callbacks in dependencies

  // WebSocket listeners for chat updates
  useEffect(() => {
    if (socket && isConnected) {
      console.log('Dashboard: Socket connected, setting up listeners...');

      // Listener for new messages
      const handleNewMessage = (messageData) => {
        console.log('Dashboard: Received new_message event, fetching unread count.');
        fetchUnreadMessages();
      };

      // Listener for when messages are marked read elsewhere
      const handleChatStatusUpdate = (data) => {
         console.log('Dashboard: Received chat_status_updated event, fetching unread count.');
         // Check if the update is relevant for the current user (optional, backend sends to specific user room)
         // if (data.userId === user?.id) {
            fetchUnreadMessages();
         // }
      };

      socket.on('new_message', handleNewMessage);
      socket.on('chat_status_updated', handleChatStatusUpdate); // Add listener for status updates

      return () => {
        if (socket) {
          socket.off('new_message', handleNewMessage);
          socket.off('chat_status_updated', handleChatStatusUpdate); // Remove listener
          console.log('Dashboard: Removed chat listeners.');
        }
      };
    } else {
       console.log('Dashboard: Socket not connected, listeners not attached.');
    }
  }, [socket, isConnected, fetchUnreadMessages]); // Depend on socket, connection status, and the fetch function


  // State for company name
  const [companyName, setCompanyName] = useState("leadbooster.");

  // Fetch company settings (logo and name)
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const config = { headers: { 'x-auth-token': token } };
        const response = await axios.get('/api/settings/company', config);

        // Set company name
        if (response.data.company_name) {
          setCompanyName(response.data.company_name);
        }

        // Set company logo
        let logoToUse = null;
        let logoSource = 'primary';

        if (response.data.logo_usage) {
          try {
            let logoUsage = typeof response.data.logo_usage === 'string'
              ? JSON.parse(response.data.logo_usage)
              : response.data.logo_usage;

            if (logoUsage && logoUsage.sidebar === 'alternative' && response.data.company_logo_alt) {
              logoSource = 'alternative';
              if (response.data.alt_logo_details?.path) {
                logoToUse = response.data.alt_logo_details.path;
              } else if (response.data.company_logo_alt) {
                // Check for Cloudinary URLs first (alternative logo)
                if (response.data.company_logo_alt.includes('cloudinary.com')) {
                  logoToUse = response.data.company_logo_alt;
                } 
                // Then check for local file paths
                else if (response.data.company_logo_alt.startsWith('/uploads/')) {
                  logoToUse = response.data.company_logo_alt;
                }
              }
            } else {
              // Try logo_details first
              if (response.data.logo_details?.path) {
                logoToUse = response.data.logo_details.path;
              } 
              // Then try company_logo directly
              else if (response.data.company_logo) {
                logoToUse = response.data.company_logo;
              }
            }
          } catch (parseError) {
            console.error('Error parsing logo usage configuration:', parseError);
            // Try logo_details first  
            if (response.data.logo_details?.path) {
              logoToUse = response.data.logo_details.path;
            }
            // Try direct company_logo property
            else if (response.data.company_logo) {
              logoToUse = response.data.company_logo;
              console.log('Fallback to company_logo directly:', logoToUse);
            }
          }
        } else {
          // Try logo_details first
          if (response.data.logo_details?.path) {
            logoToUse = response.data.logo_details.path;
          }
          // Try direct company_logo property
          else if (response.data.company_logo) {
            logoToUse = response.data.company_logo;
            console.log('Found company_logo directly:', logoToUse);
          }
        }

        if (logoToUse) {
          // IMPORTANT: Always prioritize Cloudinary URLs first
          if (logoToUse.includes('cloudinary.com')) {
            // No need to modify Cloudinary URLs, they're already fully formed
            console.log('Using Cloudinary URL directly for dashboard:', logoToUse);
          } 
          // Only fall back to local files if not a Cloudinary URL
          else if (logoToUse.startsWith('/uploads/')) {
            console.log('Falling back to local file for dashboard logo:', logoToUse);
            const isDevEnvironment = process.env.NODE_ENV === 'development';
            if (isDevEnvironment) {
              logoToUse = `http://localhost:5005${logoToUse}`;
            }
          }
          console.log(`Dashboard loading ${logoSource} logo from:`, logoToUse);
          setCompanyLogo(logoToUse);
        }
        
        // Set sidebar color if available
        if (response.data.sidebar_color) {
          setSidebarColor(response.data.sidebar_color);
          console.log('Setting sidebar color from company settings:', response.data.sidebar_color);
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
      }
    };
    fetchCompanySettings();
  }, []);

  // Click outside and escape key handler for profile menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuOpen && !e.target.closest('.profile-menu-container')) {
        setProfileMenuOpen(false);
      }
    };
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && profileMenuOpen) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [profileMenuOpen]);

  // Fetch current user data on initial load
  useEffect(() => {
    setLoading(true); // Set loading true when starting fetch
    fetchUserProfile();
  }, []); // Run only once on mount

  // Handle logout
  const handleLogout = () => {
    // Clear user data and redirect
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    
    // The Cal.com button removal is handled in the useEffect cleanup
    // when showBookingButton becomes false due to logout
  };

  // Handle dismiss booking button
  const handleDismissBooking = () => {
    localStorage.setItem('calButtonDismissed', 'true');
    setShowBookingButton(false);
    setShowDismissConfirm(false);
    // Remove the dismiss button
    const dismissBtns = document.querySelectorAll('button[title="Hide booking button permanently"]');
    dismissBtns.forEach(btn => btn.remove());
  };

  // Check if the current path matches the navigation item
  const isActive = (path) => {
    return location.pathname === path ||
      (path !== '/' && location.pathname.startsWith(path));
  };

  // Update document title based on current path
  useEffect(() => {
    if (location.pathname.match(/^\/leads\/\d+$/)) {
      return; // LeadView handles its own title
    }
    const path = Object.keys(PAGE_TITLES).find(p =>
      location.pathname === p ||
      (p !== '/' && location.pathname.startsWith(p))
    );
    const title = path ? PAGE_TITLES[path] : DEFAULT_TITLE;
    setDocumentTitle(title);
    return () => { document.title = DEFAULT_TITLE; };
  }, [location.pathname]);

  // Toggle desktop sidebar minimize state and save to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarMinimized;
    setSidebarMinimized(newState);
    localStorage.setItem('sidebarMinimized', newState.toString());
  };

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Common Navigation Links Component (to avoid duplication)
  const NavigationLinks = ({ isMobile }) => {
    return (
    <nav className={`mt-5 ${isMobile ? 'px-2' : 'flex-1 px-2'} space-y-1`}>
      {hasPermission('leads', 'access') && (
        <Link
          to="/leads"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/leads')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Leads"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Leads</span>}
        </Link>
      )}
      {hasPermission('deals', 'access') && (
        <Link
          to="/deals"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/deals')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Deals"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Deals</span>}
        </Link>
      )}
      {hasPermission('calendar', 'access') && (
        <Link
          to="/calendar"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/calendar')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Calendar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Calendar</span>}
        </Link>
      )}
      {hasPermission('tasks', 'access') && (
        <Link
          to="/tasks"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/tasks')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Tasks"
        >
          <div className="relative">
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
            </svg>
            {sidebarMinimized && !isMobile && overdueTasks > 0 && (
              <span className="absolute -top-1 -right-2 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold leading-none text-red-100 bg-red-600 rounded-full">
                {overdueTasks > 99 ? '99+' : overdueTasks}
              </span>
            )}
          </div>
          {(!sidebarMinimized || isMobile) && (
            <div className="flex items-center">
              <span className="truncate">Tasks</span>
              {overdueTasks > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                  {overdueTasks > 99 ? '99+' : overdueTasks}
                </span>
              )}
            </div>
          )}
        </Link>
      )}
      {hasPermission('kpis', 'access') && (
        <Link
          to="/kpis"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/kpis')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="KPIs"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">KPIs</span>}
        </Link>
      )}
      {hasPermission('employees', 'access') && (
        <Link
          to="/employees"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/employees')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Employees"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Employees</span>}
        </Link>
      )}
      {hasPermission('mailer', 'access') && (
        <Link
          to="/email-marketing"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/email-marketing')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Email Marketing"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Email Marketing</span>}
        </Link>
      )}
      {hasPermission('chat', 'access') && (
        <Link
          to="/chat"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/chat')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Chat - Team & Direct Messages"
          onClick={() => {
            // No need to manually mark read here, ChatRoom component handles it
            // if (unreadMessages > 0) { setUnreadMessages(0); } // Optimistic UI update removed
          }}
        >
          <div className="relative">
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
              <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
            </svg>
            {sidebarMinimized && !isMobile && unreadMessages > 0 && (
              <span className="absolute -top-1 -right-2 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold leading-none text-red-100 bg-red-600 rounded-full">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            )}
          </div>
          {(!sidebarMinimized || isMobile) && (
            <div className="flex items-center">
              <span className="truncate">Chat</span>
              {unreadMessages > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </div>
          )}
        </Link>
      )}
      
      {moduleSettings.videoCourses && hasPermission('videos', 'access') && (
        <Link
          to="/video-courses"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/video-courses')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Video Courses"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            <path d="M14 6a2 2 0 012-2h2a2 2 0 012 2v8a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Video Courses</span>}
        </Link>
      )}
      {/* Company Prospector - Replaces the old Brreg Explorer */}
      {moduleSettings.prospector && hasPermission('prospector', 'access') && (
        <Link
          to="/prospector"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/prospector') || isActive('/brreg-explorer')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Company Prospector"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Prospector</span>}
        </Link>
      )}
      {hasPermission('settings', 'access') && (
        <Link
          to="/settings"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 ${
            isActive('/settings')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-white hover:bg-blue-700 hover:text-white'
          }`}
          title="Settings"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Settings</span>}
        </Link>
      )}
      {user?.id === 1 && ( // Platform Admin Link - Conditionally Rendered
         <Link
          to="/platform/companies"
          className={`flex items-center ${isMobile ? 'px-4' : (sidebarMinimized ? 'justify-center' : 'px-4')} py-2.5 rounded-lg transition-all duration-200 mt-4 border-t border-blue-700 pt-4 ${
            isActive('/platform/companies')
              ? 'bg-blue-700 text-white font-medium'
              : 'text-blue-200 hover:bg-blue-700 hover:text-white'
          }`}
          title="Platform Admin"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${sidebarMinimized && !isMobile ? '' : 'mr-3'}`} viewBox="0 0 20 20" fill="currentColor">
             <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
          </svg>
          {(!sidebarMinimized || isMobile) && <span className="truncate">Platform Admin</span>}
        </Link>
      )}
    </nav>
    );
  };

  // Common Profile Section Component
  const ProfileSection = ({ isMobile }) => (
    <div className={`flex-shrink-0 flex border-t border-blue-700 ${isMobile ? 'p-4' : 'p-3'}`}>
      {!isMobile && (
        <div className={`absolute bottom-[70px] left-0 right-0 text-xs text-blue-300 text-center mb-2 ${sidebarMinimized ? 'opacity-50' : ''}`}>
          v0.11
        </div>
      )}

      {(!sidebarMinimized || isMobile) ? ( // Expanded view for mobile or non-minimized desktop
        <div className="relative profile-menu-container w-full">
          <button
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
            className={`w-full flex items-center justify-between px-2 py-2 bg-blue-900 bg-opacity-30 rounded-lg hover:bg-blue-900 hover:bg-opacity-50 transition-all duration-200`}
            title="Menu"
          >
            <div className="flex items-center">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white mr-2">
                {!loading && user.name && user.name.charAt(0).toUpperCase()}
              </div>
              <span className={`text-white text-sm font-medium truncate ${isMobile ? 'max-w-[150px]' : 'max-w-[95px]'}`}>
                {loading ? 'Loading...' : user.name}
              </span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ transform: profileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>

          {profileMenuOpen && (
            <div className="absolute bottom-full left-0 w-full mb-1 bg-white rounded-md shadow-lg border border-gray-200 z-20">
              <button
                onClick={() => {
                  setProfileMenuOpen(false);
                  setProfileModalOpen(true);
                }}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center rounded-t-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center rounded-b-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414A1 1 0 0015.414 7L12 3.586A1 1 0 0010.586 3H3zm9 2.414L15.414 7H12V5.414z" />
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v1a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 10v-4a1 1 0 011-1h12a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1z" />
                </svg>
                Log Out
              </button>
            </div>
          )}
        </div>
      ) : ( // Minimized desktop view
        <div className="relative profile-menu-container">
          <button
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
            className="flex flex-col items-center w-full p-1 rounded-md hover:bg-blue-900 hover:bg-opacity-30 transition-all duration-200"
            title={user.name}
          >
            <div
              className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white"
            >
              {!loading && user.name && user.name.charAt(0).toUpperCase()}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white mt-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ transform: profileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>

          {profileMenuOpen && (
            <div className="absolute bottom-full left-0 mb-1 bg-white rounded-md shadow-lg border border-gray-200 z-20 w-32 right-[-8px]">
              <button
                onClick={() => {
                  setProfileMenuOpen(false);
                  setProfileModalOpen(true);
                }}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center rounded-t-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center rounded-b-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414A1 1 0 0015.414 7L12 3.586A1 1 0 0010.586 3H3zm9 2.414L15.414 7H12V5.414z" />
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v1a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 10v-4a1 1 0 011-1h12a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1z" />
                </svg>
                Log Out
              </button>
            </div>
          )}
        </div>
      )}

      <UserProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        userId={user?.id}
      />
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">

      {/* Mobile Menu Overlay & Sidebar */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 md:hidden" onClose={setMobileMenuOpen}>
          {/* Overlay */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 flex z-40">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel 
                className="relative flex-1 flex flex-col max-w-xs w-full"
                style={{ backgroundColor: sidebarColor }}
              >
                {/* Close button for mobile */}
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </Transition.Child>

                {/* Mobile Sidebar Content */}
                <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                  {/* Logo Section */}
                  <div className="flex-shrink-0 flex items-center px-4 mb-6">
                    {companyLogo ? (
                      <div className="w-40 h-10 flex items-center justify-center overflow-hidden">
                        <img
                          src={companyLogo}
                          alt="Company Logo"
                          className="max-w-[160px] max-h-[40px] object-contain"
                        />
                      </div>
                    ) : (
                      <div>
                        <h1 className="text-2xl font-bold text-white truncate">{companyName}</h1>
                      </div>
                    )}
                  </div>
                  {/* Navigation Links - Mobile */}
                  <NavigationLinks isMobile={true} />
                </div>
                {/* Mobile Profile Section */}
                <ProfileSection isMobile={true} />
              </Dialog.Panel>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className={`hidden md:flex md:flex-shrink-0`}>
        <div 
          className={`${sidebarMinimized ? 'w-16' : 'w-52'} flex flex-col border-r border-blue-900 shadow-sm transition-all duration-300 ease-in-out relative`}
          style={{ backgroundColor: sidebarColor }}
        >
          {/* Sidebar component */}
          <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto">
            {/* Logo Section */}
            <div className={`p-4 ${sidebarMinimized ? 'items-center justify-center' : 'mb-6'} flex flex-col`}>
              {companyLogo ? (
                <div className={`${sidebarMinimized ? 'w-10 h-10' : 'w-40 h-10'} flex items-center justify-center overflow-hidden`}>
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className={`${sidebarMinimized ? 'max-w-[40px] max-h-[40px]' : 'max-w-[160px] max-h-[40px]'} object-contain`}
                  />
                </div>
              ) : (
                <h1 className={`text-2xl font-bold text-white ${sidebarMinimized ? 'text-center mb-0' : ''} truncate`}>
                  {sidebarMinimized ? companyName.charAt(0) : companyName}
                </h1>
              )}
            </div>

            {/* Sidebar toggle button - Desktop only */}
            <button
              onClick={toggleSidebar}
              className="hidden md:block absolute -right-3 top-10 bg-white border border-blue-300 rounded-full p-1 shadow-sm hover:bg-gray-50 focus:outline-none z-[50]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                style={{ transform: sidebarMinimized ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Desktop Navigation */}
            <NavigationLinks isMobile={false} />
          </div>

          {/* Desktop Profile Section */}
          <ProfileSection isMobile={false} />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 overflow-y-auto">
        {/* Hamburger Menu Button - Mobile Only */}
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100"> {/* Adjusted background */}
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <main className="flex-1 relative z-0 focus:outline-none">
          {/* Page Content - Conditionally render Terms or Outlet */}
          {loading ? (
            <div className="flex justify-center items-center h-full py-6">
             <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
          ) : needsToSignTerms ? (
            <TermsAndConditions user={user} onTermsSigned={fetchUserProfile} />
          ) : (
            <div className="p-4">
              <Outlet />
            </div>
          )}
        </main>
      </div>
      
      {/* Confirmation Modal for dismissing booking button */}
      <ConfirmationModal
        isOpen={showDismissConfirm}
        onClose={() => setShowDismissConfirm(false)}
        onConfirm={handleDismissBooking}
        title="Hide Setup Help Button?"
        message="This will permanently hide the setup help button. You can still contact support via other means."
        confirmText="Yes, hide it"
        confirmButtonColor="red"
      />
    </div>
  );
};

export default Dashboard;